import React, { createContext, useContext, useState, useEffect } from 'react';
import { auth, db } from '../firebase';
import {
   createUserWithEmailAndPassword,
   signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  setPersistence,
  browserLocalPersistence
} from 'firebase/auth';
import { doc, setDoc, getDoc } from 'firebase/firestore';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Cloud Function URL for secure access code validation
  const ACCESS_CODE_URL = process.env.NODE_ENV === 'production'
    ? 'https://us-central1-heimdl.cloudfunctions.net/validateAccessCodes'
    : 'http://localhost:5001/heimdl/us-central1/validateAccessCodes';

  // Subscription tiers
  const SUBSCRIPTION_TIERS = {
    founder: {
      name: 'Founder',
      price: 0,
      features: ['Unlimited books', 'All features', 'Forever free'],
      limits: { books: -1 }
    },
    trial: {
      name: 'Trial',
      price: 0,
      features: ['Up to 100 books', 'Basic features', '30 days'],
      limits: { books: 100, days: 30 }
    }
  };

  async function signup({ email, password, schoolName, discountCode, accessCode, selectedPlan }) {
    // Validate access codes via secure Cloud Function
    const response = await fetch(ACCESS_CODE_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ accessCode, discountCode }),
    });

    const validation = await response.json();
    if (!response.ok || !validation.success) {
      throw new Error(validation.error || 'Invalid access code.');
    }

    const userCredential = await createUserWithEmailAndPassword(auth, email, password);

    const subscription = validation.subscription;
    const subscriptionDetails = SUBSCRIPTION_TIERS[subscription];

    const now = new Date();
    let subscriptionEndsAt = null;
    
    if (subscription === 'trial') {
      subscriptionEndsAt = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);
    }

    const userData = {
      email,
      schoolName,
      subscription,
      subscriptionDetails,
      subscriptionEndsAt,
      discountCode: discountCode || null,
      accessCode: accessCode,
      createdAt: now,
      onboardingCompleted: false,
      status: 'active'
    };

    await setDoc(doc(db, 'users', userCredential.user.uid), userData);
    return userCredential.user;
  }

  async function login(email, password) {
    // Ensure persistence is set to local before signing in
    // This guarantees the session will persist across browser closes
    await setPersistence(auth, browserLocalPersistence);
    return signInWithEmailAndPassword(auth, email, password);
  }

  async function logout() {
    return signOut(auth);
  }

  // ADD THIS FUNCTION - it was missing!
  async function refreshUserData() {
    if (!currentUser) return;
    
    try {
      const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
      if (userDoc.exists()) {
        const data = userDoc.data();
        setUserData({
          ...data,
          createdAt: data.createdAt?.toDate?.() || new Date(),
          subscriptionEndsAt: data.subscriptionEndsAt?.toDate?.() || null
        });
      }
    } catch (error) {
      console.error('Error refreshing user data:', error);
    }
  }

  const isSubscriptionActive = () => {
    if (!userData) return false;
    if (userData.subscription === 'founder') return true;
    if (userData.subscriptionEndsAt) {
      // subscriptionEndsAt is already a Date object (converted when loading from Firestore)
      return new Date() < userData.subscriptionEndsAt;
    }
    return false;
  };

  const canAddBooks = (currentBookCount = 0) => {
    if (!userData) return false;
    const limits = userData.subscriptionDetails?.limits;
    if (!limits) return false;
    if (limits.books === -1) return true;
    return currentBookCount < limits.books;
  };

  const getSubscriptionStatus = () => {
    if (!userData) return { status: 'unknown', message: 'No user data' };
    
    if (userData.subscription === 'founder') {
      return { 
        status: 'active', 
        message: 'Founder - Forever Free', 
        type: 'founder',
        daysRemaining: null
      };
    }
    
    if (userData.subscriptionEndsAt) {
      const now = new Date();
      // subscriptionEndsAt is already a Date object (converted when loading from Firestore)
      const endDate = userData.subscriptionEndsAt;
      const daysRemaining = Math.ceil((endDate - now) / (1000 * 60 * 60 * 24));
      
      if (daysRemaining > 0) {
        return {
          status: 'active',
          message: userData.subscription === 'trial' 
            ? `Trial - ${daysRemaining} days remaining`
            : `${userData.subscriptionDetails.name} - ${daysRemaining} days remaining`,
          type: userData.subscription,
          daysRemaining
        };
      } else {
        return {
          status: 'expired',
          message: 'Subscription expired',
          type: userData.subscription,
          daysRemaining: 0
        };
      }
    }
    
    return { status: 'unknown', message: 'Invalid subscription data' };
  };

  useEffect(() => {
    console.log('🔐 Setting up auth state listener...');
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      console.log('🔐 Auth state changed. User:', user ? `Logged in (${user.email})` : 'Not logged in');

      if (user) {
        setCurrentUser(user);
        try {
          const userDoc = await getDoc(doc(db, 'users', user.uid));
          if (userDoc.exists()) {
            const data = userDoc.data();
            setUserData({
              ...data,
              createdAt: data.createdAt?.toDate?.() || new Date(),
              subscriptionEndsAt: data.subscriptionEndsAt?.toDate?.() || null
            });
            console.log('✅ User data loaded successfully');
          }
        } catch (error) {
          console.error('❌ Error loading user data:', error);
          setUserData(null);
        }
      } else {
        console.log('❌ No user - clearing auth state');
        setCurrentUser(null);
        setUserData(null);
      }
      setLoading(false);
    });

    return () => {
      console.log('🔐 Cleaning up auth listener');
      unsubscribe();
    };
  }, []);

  const value = {
    currentUser,
    userData,
    loading,
    signup,
    login,
    logout,
    refreshUserData,
    isSubscriptionActive,
    canAddBooks,
    getSubscriptionStatus,
    SUBSCRIPTION_TIERS
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}